import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import {
  ClientRegisteredVendor,
  Company,
  CompanyVendor,
  ExemptVendor
} from 'src/app/classes/models';
import { ChooseExemptVendorTypeModalComponent } from 'src/app/modals/choose-exempt-vendor-type-modal/choose-exempt-vendor-type-modal.component';
import { ConfirmArchiveVendorModalComponent } from 'src/app/modals/confirm-archive-vendor-modal/confirm-archive-vendor-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ApVendorUtilityService } from 'src/app/services/utility/ap-vendor-utility.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';

@Component({
  selector: 'app-admin-compliance-client-registered-vendors',
  templateUrl: './admin-compliance-client-registered-vendors.component.html',
  styleUrl: './admin-compliance-client-registered-vendors.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminComplianceClientRegisteredVendorsComponent {
  constructor(
    private CompanyVendorService: CompanyVendorService,
    private UtilityService: UtilityService,
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private Router: Router,
    private ModalService: NgbModal,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private ApVendorUtilityService: ApVendorUtilityService,
    private cdr: ChangeDetectorRef
  ) {
    this.loadCompanyVendor();
  }

  CompanyVendor?: CompanyVendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;
  Company?: Company;

  loadCompanyVendor() {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = Number(paramMap.get('CompanyId'));
        const ClientRegisteredVendorId = Number(
          paramMap.get('ClientRegisteredVendorId')
        );

        if (!CompanyId || !ClientRegisteredVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing CompanyVendor info.',
            { returnError: true }
          );
        }

        this.CompanyVendorService.getCompanyVendor(
          this.UtilityService.stringifyObjectsInQueryObject({
            CompanyId,
            ClientRegisteredVendorId,
            isArchived: { $in: [true, false] },
            ClientRegisteredVendor: {
              Contact: {}
            },
            Company: {
              excludedAttributes: ['logo']
            }
          })
        ).subscribe({
          next: (CompanyVendor: CompanyVendor) => {
            this.CompanyVendor = CompanyVendor;

            if (!this.CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing CompanyVendor.',
                { returnError: true }
              );
            }

            const { ClientRegisteredVendor, Company } = this.CompanyVendor;

            if (ClientRegisteredVendor) {
              this.ClientRegisteredVendor = ClientRegisteredVendor;
            }

            if (Company) {
              this.Company = Company;
            }

            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  addEditClientRegisteredVendor() {
    this.Router.navigate(['overview'], { relativeTo: this.route });
  }

  viewCompliance() {
    this.Router.navigate(['compliance'], { relativeTo: this.route });
  }

  viewDocumentsAndNotes() {
    this.Router.navigate(['documents-notes'], { relativeTo: this.route });
  }

  updateEINAutoLink() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update EIN Auto link. Missing CompanyVendor record.',
        { returnError: true }
      );
    }

    const { CompanyId, ClientRegisteredVendorId, allowEinAutoLink } =
      this.CompanyVendor;

    if (!CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing client id.', {
        returnError: true
      });
    }

    if (!ClientRegisteredVendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing vendor id.', {
        returnError: true
      });
    }

    this.ApVendorUtilityService.handleApAutolinkDisableModal(
      !!allowEinAutoLink,
      { CompanyId, ClientRegisteredVendorId }
    ).subscribe({
      next: () => {
        from(
          companyVendorsService.updateOneById({
            pathParams: { companyVendorId: this.CompanyVendor!.id! },
            data: { allowEinAutoLink: !this.CompanyVendor!.allowEinAutoLink }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully updated EIN Auto link.'
            );
            this.loadCompanyVendor();
            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  convertVendorToComplianceExempt() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing client/vendor information.',
        { returnError: true }
      );
    }

    const { id: CompanyVendorId } = this.CompanyVendor;

    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing client information.',
        { returnError: true }
      );
    }

    const { id: CompanyId } = this.Company;

    if (!this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor information.',
        { returnError: true }
      );
    }

    const { id: ClientRegisteredVendorId } = this.ClientRegisteredVendor;

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true, size: 'md', backdrop: 'static' }
    );
    Object.assign(componentInstance, {
      displayText:
        'Are you sure you want to convert vendor to compliance exempt?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          const { componentInstance, result } = this.ModalService.open(
            ChooseExemptVendorTypeModalComponent,
            { centered: true, size: 'md', backdrop: 'static' }
          );
          Object.assign(componentInstance, { CompanyId });

          from(result).subscribe({
            next: (ExemptVendorTypeId) => {
              if (ExemptVendorTypeId) {
                this.ClientRegisteredVendorService.convertClientRegisteredVendorToComplianceExempt(
                  { CompanyId, ClientRegisteredVendorId, ExemptVendorTypeId }
                ).subscribe({
                  next: (ExemptVendor: ExemptVendor) => {
                    const { id: ExemptVendorId } = ExemptVendor;
                    this.Router.navigate([
                      'admin',
                      'run-vive',
                      'client-vendor',
                      CompanyVendorId,
                      'client',
                      CompanyId,
                      'exempt-vendor',
                      ExemptVendorId,
                      'action',
                      'edit',
                      'overview'
                    ]);
                  }
                });
              }
            }
          });
        }
      }
    });
  }

  archiveClientVendor() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot archive Client/Vendor relationship. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmArchiveVendorModalComponent,
      { backdrop: 'static', size: 'lg' }
    );
    Object.assign(componentInstance, { modelType: 'companyVendor' });

    from(result).subscribe({
      next: (note) => {
        if (note) {
          this.CompanyVendorService.saveCompanyVendor(
            Object.assign(this.CompanyVendor!, {
              isArchived: true,
              Notes: [note]
            })
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success!',
                'Successfully archived client vendor.'
              );
              this.cdr.markForCheck();
              this.Router.navigate(['admin', 'run-vive', { viveStatus: '' }]);
            }
          });
        }
      }
    });
  }
}
